import React from 'react';
import { AlertTriangle, Heart, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { client, urlFor, getImageAlt } from '../../lib/sanity';
import { getPetUrlPath } from '../../lib/petUtils';
import type { Pet } from '../../types/sanity';

export function EmergencyFundraiser() {
  const [urgentPet, setUrgentPet] = React.useState<Pet | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchUrgentPet = async () => {
      try {
        const result = await client.fetch<Pet[]>(`
          *[_type == "pet" && isUrgentFundraising == true && !(_id in path("drafts.**"))] | order(_createdAt desc)[0...1] {
            _id,
            name,
            slug,
            image,
            fundraisingGoal,
            fundraisingCurrent,
            fundraisingDescription,
            description,
            mainCategory
          }
        `);
        if (result.length > 0) {
          setUrgentPet(result[0]);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching urgent pet:', err);
        setLoading(false);
      }
    };

    fetchUrgentPet();
  }, []);

  if (loading || !urgentPet) {
    return null;
  }

  const percentage = urgentPet.fundraisingGoal ? 
    Math.round((urgentPet.fundraisingCurrent || 0) / urgentPet.fundraisingGoal * 100) : 0;

  return (
    <section className="py-16 px-8 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="bg-accessible-red-600/10 rounded-xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 -translate-y-1/2 translate-x-1/2">
            <div className="w-full h-full rounded-full bg-accessible-red-600/20 animate-pulse"></div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="md:w-1/3 relative">
              <div className="aspect-square rounded-lg overflow-hidden">
                <img 
                  src={urlFor(urgentPet.image).width(400).height(400).url()}
                  alt={getImageAlt(urgentPet.image, urgentPet)}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-4 -left-4 bg-accessible-red-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" aria-hidden="true" />
                <span className="font-bold">PILNE</span>
              </div>
            </div>
            
            <div className="md:w-2/3">
              <h2 className="heading-2 text-accessible-red-600 mb-4">
                {urgentPet.name} potrzebuje pomocy!
              </h2>
              <p className="body-large urgent-fundraising-color mb-4">
                {urgentPet.fundraisingDescription || urgentPet.description}
              </p>
              
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="label-large text-accessible-gray-900 dark:text-white high-contrast:text-white">
                    Zebrano: {(urgentPet.fundraisingCurrent || 0).toLocaleString('pl-PL')} zł z {(urgentPet.fundraisingGoal || 0).toLocaleString('pl-PL')} zł
                  </span>
                  <span className="label-medium text-accessible-gray-900 dark:text-white high-contrast:text-white">
                    {percentage}%
                  </span>
                </div>
                <div className="h-3 bg-accessible-gray-200 dark:bg-accessible-gray-600 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-accessible-red-600 rounded-full transition-all duration-1000 ease-out" 
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/wplata-jednorazowa"
                  className="bg-accessible-red-600 text-white px-8 py-4 rounded-lg hover:bg-accessible-red-700 transition-colors flex items-center justify-center gap-2 shadow-lg btn-text-large"
                >
                  <Heart className="w-5 h-5" aria-hidden="true" />
                  <span>POMÓŻ {urgentPet.name.toUpperCase()}</span>
                </Link>
                <Link
                  to={getPetUrlPath(urgentPet)}
                  className="bg-white dark:bg-accessible-gray-700 high-contrast:bg-black text-accessible-gray-900 dark:text-white high-contrast:text-white px-8 py-4 rounded-lg hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-600 high-contrast:hover:bg-white/10 transition-colors flex items-center justify-center gap-2 border border-accessible-gray-200 dark:border-accessible-gray-600 high-contrast:border-white btn-text-large"
                >
                  <span>Poznaj historię {urgentPet.name}</span>
                  <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}